import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Use useRef to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL hash
        const hash = location.hash || window.location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          console.error('No session_id found in URL');
          navigate('/', { replace: true });
          return;
        }

        const sessionId = sessionIdMatch[1];

        // Exchange session_id for session_token with retry logic
        // Deployed environments may have Cloudflare challenges or transient issues
        let response = null;
        let lastError = null;
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            response = await axios.post(`${API}/auth/session`, {
              session_id: sessionId
            }, {
              // Don't send any existing (stale) auth token for this request
              headers: { 'X-Skip-Auth': '1' },
              timeout: 15000
            });
            break; // Success
          } catch (err) {
            lastError = err;
            console.warn(`Auth session exchange attempt ${attempt + 1}/3 failed:`, err.message);
            if (attempt < 2) {
              await new Promise(r => setTimeout(r, 1000 * (attempt + 1))); // 1s, 2s backoff
            }
          }
        }

        if (!response) {
          console.error('All auth session exchange attempts failed:', lastError);
          navigate('/', { replace: true });
          return;
        }

        const userData = response.data;
        
        // Store session token in localStorage for Bearer auth (cookies unreliable cross-domain)
        if (userData.session_token) {
          localStorage.setItem('session_token', userData.session_token);
        } else {
          console.error('No session_token in response');
          navigate('/', { replace: true });
          return;
        }
        
        updateUser(userData);

        // Clear the hash from URL
        window.history.replaceState(null, '', window.location.pathname);

        // Redirect based on user status and role
        if (userData.role === 'admin' || userData.role === 'super_admin') {
          navigate('/admin', { replace: true, state: { user: userData } });
        } else if (userData.status === 'approved') {
          navigate('/dashboard', { replace: true, state: { user: userData } });
        } else {
          navigate('/pending', { replace: true, state: { user: userData } });
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        navigate('/', { replace: true });
      }
    };

    processAuth();
  }, [location, navigate, updateUser]);

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
      <div className="text-center" data-testid="auth-callback-loading">
        <div className="spinner mx-auto mb-4"></div>
        <p className="text-slate-600">Signing you in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
